import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { PushPipe } from '@ngrx/component';
import { Answer } from '@wdx/clmi/clmi-swagger-gen';
import { REJECT_ANSWERS } from '@wdx/clmi/utils/constants';
import { WdxButtonStyle } from '@wdx/shared/components/wdx-button';
import {
    WdxDialogComponent,
    WdxDialogService,
} from '@wdx/shared/components/wdx-dialog';
import { WdxFormFieldModule } from '@wdx/shared/components/wdx-form-field';
import { WdxInputTextModule } from '@wdx/shared/components/wdx-input-text';
import {
    WdxMultiSelectComponent,
    WdxMultiSelectOptions,
} from '@wdx/shared/components/wdx-multi-select';
import {
    TranslateTokenisedStringPipe,
    TRANSLATION_CANCEL_BTN,
    TRANSLATION_CONFIRM_BTN,
    TRANSLATION_DIALOG_COMMENT_BODY_REJECT,
    TRANSLATION_DIALOG_TITLE_REJECT_ITEM,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { Observable } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { AnswersFacadeService } from '../../../state/answers/answers-facade/answers-facade.service';
import { ICommentFormControl } from './work-item-comment.interface';
import { WorkItemDialogService } from './work-item-comment.service';

@Component({
    selector: 'clmi-ui-work-item-comment',
    imports: [
        WdxDialogComponent,
        WdxInputTextModule,
        ReactiveFormsModule,
        WdxMultiSelectComponent,
        PushPipe,
        WdxFormFieldModule,
        TranslateTokenisedStringPipe,
    ],
    templateUrl: './work-item-comment.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CLMiWorkItemCommentComponent
    extends WdxDestroyClass
    implements OnInit, AfterViewInit
{
    private wdxDialogService = inject(WdxDialogService);
    private workItemDialogService = inject(WorkItemDialogService);
    private answersFacade = inject(AnswersFacadeService);
    private fb = inject(FormBuilder);

    readonly DIALOG_TITLE_REJECT_ITEM = TRANSLATION_DIALOG_TITLE_REJECT_ITEM;
    readonly DIALOG_COMMENT_BODY_REJECT =
        TRANSLATION_DIALOG_COMMENT_BODY_REJECT;
    readonly CANCEL_BTN = TRANSLATION_CANCEL_BTN;
    readonly CONFIRM_BTN = TRANSLATION_CONFIRM_BTN;

    commentForm!: FormGroup<ICommentFormControl>;
    rejectionReasons$: Observable<WdxMultiSelectOptions[]> = this.answersFacade
        .getAnswer$(REJECT_ANSWERS)
        .pipe(
            map((reasons: Answer[]) =>
                reasons
                    .filter(({ isActive }) => isActive)
                    .map(({ name }) => ({
                        label: name,
                        value: name,
                    })),
            ),
        );
    readonly WDX_BUTTON_STYLE = WdxButtonStyle;

    ngOnInit(): void {
        this.commentForm = this.fb.group({
            comment: ['', [Validators.required, Validators.minLength(3)]],
            reason: ['', [Validators.required]],
        });

        this.commentForm.valueChanges
            .pipe(debounceTime(150), takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.workItemDialogService.updateFormValue(
                        this.commentForm.value,
                    );
                    this.wdxDialogService.disableButtons$.next(
                        this.commentForm.invalid,
                    );
                },
            });
    }

    ngAfterViewInit(): void {
        this.wdxDialogService.disableButtons$.next(true);
    }

    onClick(): void {
        this.workItemDialogService.submitForm();
    }
}
